import React from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Button,
  Box,
  Alert,
} from "@mui/material";
import { Delete, Add, DeleteOutline } from "@mui/icons-material";
import useStyles from "../../../../components/common/table/styles";
import Toggle from "../../../../components/common/toggle/Toggle";

const CancellationFees = ({ formData, setFormData, setIsChanged }: any) => {
  const classes = useStyles();

  const tableHeadings = [
    { label: "Days before work was to commence", align: "center" },
    { label: "Percentage of the total quoted price (%)", align: "center" },
    { label: "Action", align: "center" },
  ];

  // ✅ Always ensure one empty row exists
  const fees = formData?.cancellation_fees?.length
    ? formData.cancellation_fees
    : [{ days: "", percent: "" }];

  // ✅ Handle input change
  const handleInputChange = (index: number, field: string, value: string) => {
    const updated = fees.map((item: any) => ({ ...item }));
    updated[index][field] = value;

    // Auto-add new blank row when last one filled
    // const lastRow = updated[updated.length - 1];
    // if (lastRow.days && lastRow.percent) {
    //   updated.push({ days: "", percent: "" });
    // }

    setFormData((prev: any) => ({
      ...prev,
      cancellation_fees: updated,
    }));
    setIsChanged(true);
  };

  // ✅ Handle delete row
  const handleDelete = (index: number) => {
    const updated = fees.filter((_: any, i: number) => i !== index);
    if (updated.length === 0) updated.push({ days: "", percent: "" });

    setFormData((prev: any) => ({
      ...prev,
      cancellation_fees: updated,
    }));
    setIsChanged(true);
  };

  // ✅ Add new empty row manually
  const handleAddRow = () => {
    setFormData((prev: any) => ({
      ...prev,
      cancellation_fees: [...fees, { days: "", percent: "" }],
    }));
    setIsChanged(true);
  };

  return (
    <Grid item xs={12} md={12}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "16px",
          backgroundColor: "#f8f9fa",
          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        }}
      >
        <Typography
          sx={{
            textTransform: "uppercase",
            fontWeight: 600,
            color: "#02906F",
            mb: 2,
            fontSize: "1rem",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          Cancellation Fees
          {/* add toggle button bot cancellation */}
          <span>
            <Toggle
              checked={formData?.is_cancellation_fees_allowed}
              onChange={() => {
                setFormData((prev: any) => ({
                  ...prev,
                  is_cancellation_fees_allowed:
                    !prev.is_cancellation_fees_allowed,
                }));
                // setIsChanged(true);
              }}
              name="Is Cancellation Fees Allowed"
            />
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: "0.875rem",
                color: "#637381",
                fontWeight: 600,
              }}
            >
              Is Cancellation Fees Allowed
            </Typography>
          </span>
        </Typography>
        {formData?.is_cancellation_fees_allowed && (
          <>
            <TableContainer component={Paper} sx={{ borderRadius: "12px" }}>
              <Table
                sx={{ minWidth: 700 }}
                aria-label="cancellation-fees-table"
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: "#02906F",
                      "& th": {
                        color: "#fff",
                        fontWeight: 600,
                        textAlign: "center",
                        padding: "10px",
                      },
                    }}
                  >
                    {tableHeadings.map((head, i) => (
                      <TableCell key={i}>{head.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {fees.map((row: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: "#e9f6f3" },
                        transition: "background 0.3s",
                      }}
                    >
                      <TableCell
                        sx={{
                          textAlign: "center",
                          padding: "0px 16px",
                          height: "0px",
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          type="text"
                          value={row.days}
                          onChange={(e) => {
                            const value = e.target.value;

                            // Allow only digits and max 3 characters
                            if (/^\d{0,3}$/.test(value)) {
                              handleInputChange(index, "days", value);
                            }
                          }}
                          inputProps={{
                            inputMode: "numeric",
                            style: { textAlign: "left", height: "16px" },
                          }}
                          
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          padding: "4px 16px",
                          height: "0px",
                        }}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          type="text"
                          value={row.percent}
                          onChange={(e) => {
                            const value = e.target.value;
                            const PERCENTAGE = /^(\d{0,2})(\.\d{0,2})?$/;

                            if (value === "" || PERCENTAGE.test(value)) {
                              handleInputChange(index, "percent", value);
                            }
                          }}
                          inputProps={{
                            inputMode: "decimal",
                            style: { textAlign: "left", height: "16px" },
                          }}
                          onKeyDown={(e) => {
                            if (["e", "E", "+", "-"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          padding: "0px 16px",
                          height: "0px",
                        }}
                      >
                        {fees.length > 1 && (
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(index)}
                          >
                            <DeleteOutline />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ✅ Add Button */}
            {fees.length < 2 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddRow}
                  sx={{
                    //   backgroundColor: "#02906F",
                    //   "&:hover": { backgroundColor: "#037d62" },
                    textTransform: "capitalize",
                    fontWeight: 500,
                    px: 3,
                    py: 0.5,
                    borderRadius: "8px",
                  }}
                >
                  Add More
                </Button>
              </Box>
            )}
            <Alert sx={{ mt: 2 }} severity="info">
              <b>
                This breach message will be shown on the customer portal confirm
                section
                <br />
                Warning:
              </b>{" "}
              You should be aware that setting the date as Confirmed and then
              moving it up to {"{{days-1}}"} before the service date will incur
              a cost of £{"{{total_quote_value*Percent-1}}"} and moving the date
              up to
              {"{{days-2}}"} days before the service date will incur a charge of
              £{"{{total_quote_value*Percent-2}}"}.
            </Alert>
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default CancellationFees;
